import { useEffect, useRef } from "react";

interface InfiniteScrollSentinelProps {
  /** Called when the sentinel becomes visible. Wire to fetchNextPage. */
  onVisible: () => void;
  /** Disable the observer when the consumer can't load more (e.g. no more pages, or already fetching). */
  disabled?: boolean;
  /** rootMargin for the IntersectionObserver — accepts "100px" / "10%". */
  rootMargin?: string;
}

/**
 * Drop-in sentinel for cursor-paginated lists. Renders a tiny invisible div
 * at the bottom of the list; an IntersectionObserver fires `onVisible` when
 * it scrolls into view, which the consumer typically wires to
 * `fetchNextPage` from useCursorInfinite.
 *
 * Guarded against firing while disabled (e.g. during in-flight fetches or
 * when the cursor terminates) so consumers can pass
 * `hasNextPage && !isFetchingNextPage` straightforwardly.
 */
export function InfiniteScrollSentinel({
  onVisible,
  disabled,
  rootMargin = "200px",
}: InfiniteScrollSentinelProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  // Latest onVisible in a ref so the observer effect only re-creates when
  // disabled/rootMargin changes, not on every render of the parent.
  const onVisibleRef = useRef(onVisible);
  onVisibleRef.current = onVisible;

  useEffect(() => {
    const node = ref.current;
    if (!node || disabled) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            onVisibleRef.current();
            break;
          }
        }
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [disabled, rootMargin]);

  return <div ref={ref} aria-hidden="true" style={{ height: 1 }} />;
}
